import { useQuery } from "@tanstack/react-query";
import { apiGet, apiPost, getToken } from "./api";
import { useSession, setSession } from "./auth";
import { readStoredSession, writeStoredSession, type AuthSession } from "@/features/auth/api";
import { queryClient } from "./queryClient";
import { sameTokenScope } from "./session-scope";

export type AccountType = "personal" | "business" | "unclassified";
export const accountTypeLabel = (type?: AccountType) => type === "personal" ? "個人・無料" : type === "business" ? "法人・広告なし" : "利用区分未設定";
export interface WorkspaceRecord { id: string; tenant_id?: string; name: string; slug?: string; default_language?: string; plan?: string; role?: string; active?: boolean; switchable?: boolean; account_type?: AccountType; ad_eligible?: boolean; retention_days?: number | null; retention_max_days?: number | null; timezone?: string; branding?: { show_logo: boolean; show_company_footer: boolean }; }
/** Personal workspaces purge history, attachments and the search index after this many days (server: PERSONAL_RETENTION_MAX_DAYS). */
export const PERSONAL_RETENTION_MAX_DAYS = 90;
export const personalRetentionNotice = (maxDays: number | null | undefined = PERSONAL_RETENTION_MAX_DAYS) => `個人ワークスペースは履歴・添付・検索索引を ${maxDays ?? PERSONAL_RETENTION_MAX_DAYS} 日で自動削除します`;
export interface MemberRecord { id: string; email: string; display_name: string | null; role: string; avatar_url?: string | null; }
export function useWorkspaces() {
  const session = useSession();
  return useQuery({ queryKey: ["workspaces", session?.user.id], enabled: !!session,
    queryFn: async () => (await apiGet<{ workspaces: WorkspaceRecord[] }>("/v1/workspaces")).workspaces });
}
export function useWorkspaceMemberships() {
  const session=useSession();
  return useQuery({queryKey:["workspace-memberships",session?.user.id,session?.tenantId],enabled:!!session,
    queryFn:()=>apiGet<{active_workspace_id:string;workspaces:WorkspaceRecord[]}>("/v1/me/workspaces")});
}
export class WorkspaceMfaRequired extends Error {
  constructor(public challenge: object) { super("追加の本人確認が必要です。"); }
}
export async function applyWorkspaceSession(result: Partial<AuthSession> & {mfa_required?:boolean}, previousToken=getToken()): Promise<void> {
  if(result.mfa_required)throw new WorkspaceMfaRequired(result);
  if(!result.access_token || !result.refresh_token || !result.user) throw new Error("追加の本人確認が必要です。再ログインしてからワークスペースを切り替えてください。");
  await queryClient.cancelQueries();
  if(!sameTokenScope(previousToken,getToken()))throw new Error("別の画面でログイン先が変更されました。もう一度ワークスペースを選択してください。");
  writeStoredSession(result as AuthSession);
  setSession({token:result.access_token,tenantId:result.user.tenant_id,user:{id:result.user.id,email:result.user.email,displayName:result.user.display_name || result.user.email}});
}
export async function switchWorkspace(id: string) {
  const previousToken=getToken();
  const result=await apiPost<AuthSession & {workspace:WorkspaceRecord}>(`/v1/workspaces/${encodeURIComponent(id)}/switch`,{refresh_token:readStoredSession()?.refresh_token});
  await applyWorkspaceSession(result,previousToken); return result.workspace;
}
export async function createWorkspace(name: string, accountType: Exclude<AccountType, "unclassified"> = "personal") {
  const previousToken=getToken();
  const result=await apiPost<AuthSession & {workspace:WorkspaceRecord}>("/v1/workspaces",{name,account_type:accountType,refresh_token:readStoredSession()?.refresh_token});
  await applyWorkspaceSession(result,previousToken); return result.workspace;
}
export async function leaveWorkspace(id: string) {
  const previousToken=getToken();
  const result=await apiPost<Partial<AuthSession> & {ok:boolean}>(`/v1/workspaces/${encodeURIComponent(id)}/leave`,{});
  if(result.access_token)await applyWorkspaceSession(result,previousToken);
  else if(!result.ok)throw new WorkspaceMfaRequired(result);
  await queryClient.invalidateQueries({queryKey:["workspace-memberships"]});
}
export function useWorkspace(workspaceId?: string | null) {
  const workspaces = useWorkspaces();
  return { ...workspaces, data: workspaceId ? workspaces.data?.find(workspace => workspace.id === workspaceId) : workspaces.data?.[0] };
}
export function useMembers() {
  const workspace = useWorkspace();
  const query = useQuery({ queryKey: ["members", workspace.data?.id], enabled: !!workspace.data?.id,
    queryFn: async () => (await apiGet<{ members: MemberRecord[] }>(`/v1/workspaces/${encodeURIComponent(workspace.data!.id)}/members`)).members });
  return { ...query, workspace: workspace.data, members: (query.data || []).map(m => ({ ...m, displayName: m.display_name || m.email, avatarUrl: m.avatar_url, presence: undefined })) };
}
